/* eslint-disable react-refresh/only-export-components */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { withJsonFormsControlProps } from '@jsonforms/react';
import {
  and,
  type ControlProps,
  optionIs,
  rankWith,
  uiTypeIs,
  type JsonFormsRendererRegistryEntry,
} from '@jsonforms/core';
import { httpClient } from '@/utils/httpClient';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'SizeTypeaheadRenderer' });

const SIZE_TYPEAHEAD_VARIANT = 'sizeTypeahead';
const DEBOUNCE_MS = 250;

const SizeTypeaheadControl = ({
  data,
  handleChange,
  path,
  uischema,
  label,
  schema,
  visible = true,
  enabled = true,
  errors,
}: ControlProps) => {
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>(data ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resolvedLabel = label ?? (schema?.title as string | undefined) ?? '';
  const endpoint = (uischema.options as Record<string, unknown> | undefined)?.endpoint as
    | string
    | undefined ?? '/api/v1/rpc/catalog_size_suggestions';

  const fetchSuggestions = useCallback(
    async (prefix: string) => {
      setLoading(true);
      try {
        const url = prefix
          ? `${endpoint}?prefix=${encodeURIComponent(prefix)}`
          : endpoint;
        const result = await httpClient.get<Array<{ size: string }>>(url);
        const sizes = Array.isArray(result) ? result.map((r) => r.size) : [];
        setOptions(sizes);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load size suggestions';
        log.warn('Size typeahead fetch failed', { error: message });
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [endpoint],
  );

  // Load initial suggestions on mount
  useEffect(() => {
    void fetchSuggestions('');
  }, [fetchSuggestions]);

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, value: string) => {
      setInputValue(value);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        void fetchSuggestions(value);
      }, DEBOUNCE_MS);
    },
    [fetchSuggestions],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const currentValue = useMemo(() => {
    if (data === null || data === undefined || data === '') return null;
    return String(data);
  }, [data]);

  if (!visible) {
    return null;
  }

  return (
    <Autocomplete<string, false, false, true>
      freeSolo
      options={options}
      value={currentValue}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={(_event, newValue) => {
        const resolved = newValue?.trim() || null;
        handleChange(path, resolved);
      }}
      onBlur={() => {
        // Commit typed value on blur even if not selected from dropdown
        const trimmed = inputValue?.trim() || null;
        if (trimmed !== (data ?? null)) {
          handleChange(path, trimmed);
        }
      }}
      loading={loading}
      disabled={!enabled}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          label={resolvedLabel}
          error={Boolean(errors)}
          helperText={errors || undefined}
          fullWidth
          inputProps={{
            ...params.inputProps,
            style: { textAlign: 'left' },
          }}
        />
      )}
    />
  );
};

const SizeTypeaheadControlRenderer = withJsonFormsControlProps(SizeTypeaheadControl);

/**
 * Rank 5 renderer for size fields with type-ahead.
 * Activates for Controls with options.variant = 'sizeTypeahead'.
 */
const baseVariantTester = and(uiTypeIs('Control'), optionIs('variant', SIZE_TYPEAHEAD_VARIANT));

export const sizeTypeaheadRendererEntry: JsonFormsRendererRegistryEntry = {
  tester: rankWith(5, baseVariantTester),
  renderer: SizeTypeaheadControlRenderer,
};
